import { useCallback, useEffect } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, RefObject } from 'react';
import { getFocusableElements } from './focus';
import { lockBodyScroll } from './scrollLock';

export interface ModalSurfaceOptions {
  /** The element carrying `role="dialog"`. Focus is trapped inside it. */
  panelRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  initialFocusRef?: RefObject<HTMLElement | null>;
  closeOnEscape?: boolean;
  lockScroll?: boolean;
}

export interface ModalSurface {
  /** Attach to the outermost element of the surface, backdrop included. */
  onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => void;
}

/**
 * The behavioural half of a modal surface: focus capture and restore, the Tab
 * trap, Escape, and the body scroll lock.
 *
 * The key handler is returned rather than attached to `document` so that
 * overlays stacked on top — a menu portalled out of an open dialog — can stop
 * propagation and consume Escape first. React routes portal events through the
 * component tree, so bubbling still reaches us when it should.
 */
export function useModalSurface(options: ModalSurfaceOptions): ModalSurface {
  const { panelRef, onClose, initialFocusRef, closeOnEscape = true, lockScroll = true } = options;

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const restoreTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const releaseScroll = lockScroll ? lockBodyScroll() : null;

    const initial = initialFocusRef?.current ?? getFocusableElements(panel)[0] ?? panel;
    initial.focus({ preventScroll: true });

    return () => {
      releaseScroll?.();
      // Guard against restoring focus to a node the close removed from the DOM.
      if (restoreTo && restoreTo.isConnected) restoreTo.focus({ preventScroll: true });
    };
  }, [panelRef, initialFocusRef, lockScroll]);

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      if (event.key === 'Escape') {
        if (!closeOnEscape) return;
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;

      // Focus is moved by hand rather than by the browser so the wrap at both
      // ends is exact regardless of how the surface is nested.
      event.preventDefault();
      const focusable = getFocusableElements(panel);
      if (focusable.length === 0) {
        panel.focus({ preventScroll: true });
        return;
      }

      const active = document.activeElement;
      const current = active instanceof HTMLElement ? focusable.indexOf(active) : -1;
      const delta = event.shiftKey ? -1 : 1;
      const next =
        current === -1
          ? event.shiftKey
            ? focusable.length - 1
            : 0
          : (current + delta + focusable.length) % focusable.length;
      focusable[next].focus({ preventScroll: true });
    },
    [closeOnEscape, onClose, panelRef],
  );

  return { onKeyDown };
}
